import { Textarea } from "@/components/atoms/Textarea";
import { Button } from "@/components/atoms/Button";
import SendIcon from "@/icons/__generated__/ArrowUp/ArrowUp16";
import { ChangeEventHandler, useEffect, useRef, useState } from "react";
import {cn} from "@/lib/utils";

export const MessageInput = ({
  placeholder = '',
  value,
  onChange,
  onSendPressed,
  disableSend = false,
  showBorder = true,
  expandedSendButtonClass = '',
  shrunkSendButtonClass = '',
}: {
  placeholder?: string;
  value: string;
  onChange: ChangeEventHandler<HTMLTextAreaElement>;
  onSendPressed: () => void;
  disableSend?: boolean;
  showBorder?: boolean;
  expandedSendButtonClass?: string
  shrunkSendButtonClass?: string
}) => {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const [isExpanded, setIsExpanded] = useState(false);

  const adjustTextareaHeight = () => {
    const textarea = textareaRef.current;
    if (!textarea) return;

    // Reset height to auto to get the correct scrollHeight
    textarea.style.height = 'auto';

    // Calculate new height (24px per line, max 5 lines)
    const newHeight = Math.min(textarea.scrollHeight, 120); // 120px = 5 lines * 24px

    textarea.style.height = `${newHeight}px`;

    // Update expanded state
    setIsExpanded(newHeight > 40);

    // Scroll to bottom
    textarea.scrollTop = textarea.scrollHeight;
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [value]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      event.preventDefault();
      if (!disableSend) {
        onSendPressed();
      }
    }
  };

  return (
    <div className="tw-flex tw-items-center tw-gap-4">
      <div className={cn("tw-flex-1 tw-rounded-[20px] tw-bg-base-white tw-min-h-[40px]", showBorder && 'tw-border-[1px] tw-border-gray-400')}>
        <div className={`tw-flex ${isExpanded ? 'tw-items-end' : 'tw-items-center'} tw-h-full`}>
          <div className="tw-flex-1 tw-overflow-hidden tw-rounded-l-[20px]">
            <Textarea
              ref={textareaRef}
              placeholder={placeholder}
              fullWidth={true}
              value={value}
              onChange={onChange}
              onKeyDown={handleKeyDown}
              rows={1}
              className="focus:tw-outline-0 tw-outline-0 tw-border-0 tw-resize-none tw-min-h-[24px] tw-max-h-[120px] tw-overflow-y-auto tw-transition-all tw-duration-200 tw-rounded-l-[24px]"
            />
          </div>
          <Button
            variant="ghost"
            size="default"
            onClick={onSendPressed}
            disabled={disableSend}
            aria-label="Send message"
            className={cn(`tw-flex-shrink-0 tw-rounded-full tw-w-[32px] tw-h-[32px] tw-p-0 tw-mt-2 tw-mb-2 tw-mr-4 ${isExpanded ? 'tw-mb-4' : ''} ${!disableSend
              ? "tw-bg-purple-600 hover:tw-bg-purple-600 hover:tw-opacity-90"
              : "tw-bg-gray-400"
            }`, isExpanded ? expandedSendButtonClass : shrunkSendButtonClass)}
          >
            <SendIcon color={"#fff"}/>
          </Button>
        </div>
      </div>
    </div>
  );
};
